import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "picker" }
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "spacer" }
const _hoisted_4 = ["value", "label"]
const _hoisted_5 = {
  key: 0,
  class: "error-message"
}
const _hoisted_6 = {
  key: 0,
  class: "spacer"
}
const _hoisted_7 = ["value", "label", "options"]
const _hoisted_8 = {
  key: 1,
  class: "spacer"
}
const _hoisted_9 = ["value", "label", "options"]
const _hoisted_10 = {
  key: 2,
  class: "commits-table mt-20"
}
const _hoisted_11 = ["rows", "columns"]

import { ref, computed, reactive, watch, inject } from 'vue';
import { useStore } from 'vuex';
import { makeCommitShaCell, makeCommitAuthorCell } from '../../utils/table-formatters';
import debounce from 'lodash/debounce';
import { isArray } from '@shell/utils/array';
import { GitUtils, Commit } from '@shell/utils/git';


export default /*@__PURE__*/_defineComponent({
  __name: 'GitPicker',
  props: {
    value: {},
    type: {}
  },
  emits: ['change'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();
const t = store.getters['i18n/t'];

const debounceTime = inject<number>('debounceTime', 1000);
// State
const hasError = reactive({ acc: false, repo: false, branch: false, commits: false });
const repos = ref<object[]>([]);
const branches = ref<object[]>([]);
const commits = ref<any[]>([]);
const selectedAccOrOrg = ref<string | undefined>(props.value?.selectedAccOrOrg);
const selectedRepo = ref<object | undefined>(props.value?.selectedRepo);
const selectedRepoName = computed(() => selectedRepo.value?.name);
const selectedBranch = ref<object | undefined>(props.value?.selectedBranch);
const selectedBranchName = computed(() => selectedBranch.value?.name);
const selectedCommit = ref<Commit | null>(props.value?.selectedCommit);

// Computed
const preparedRepos = computed(() =>
  normalizeArray(repos.value, (item: any) => ({ value: item.name, label: item.name }))
);

const preparedBranches = computed(() =>
  normalizeArray(branches.value, (item: any) => ({ value: item.name, label: item.name }))
);

const preparedCommits = computed<Commit[]>(() =>
  normalizeArray(commits.value, (c: any) => GitUtils[props.type].normalize.commit(c))
);

const selectedCommitId = computed(() => selectedCommit.value?.commitId);

// Columns for trailhand-table
const columns = computed(() => [
  {
    field: 'commitId',
    label: t(`gitPicker.${ props.type }.tableHeaders.choose.label`),
    width: '60px',
    sortable: false,
    formatter: (_v: any, row: any) => {
      const input = document.createElement('input') as HTMLInputElement;

      input.type = 'radio';
      input.name = 'commit-picker';
      input.value = row.commitId || '';
      input.checked = row.commitId === selectedCommitId.value;
      input.style.cursor = 'pointer';
      input.addEventListener('change', () => final(row.commitId));

      return input;
    }
  },
  {
    field: 'sha',
    label: t(`gitPicker.${ props.type }.tableHeaders.sha.label`),
    width: '90px',
    sortable: false,
    formatter: (_v: any, row: any) => makeCommitShaCell(row)
  },
  {
    field: 'author',
    label: t(`gitPicker.${ props.type }.tableHeaders.author.label`),
    width: '190px',
    sortable: false,
    formatter: (_v: any, row: any) => makeCommitAuthorCell(
      row,
      t(`gitPicker.${ props.type }.tableHeaders.author.unknown`)
    )
  },
  {
    field: 'message',
    label: t(`gitPicker.${ props.type }.tableHeaders.message.label`),
    sortable: false,
  },
  {
    field: 'date',
    label: t(`gitPicker.${ props.type }.tableHeaders.date.label`),
    width: '220px',
    sortable: false,
    formatter: (_v: any, row: any) => {
      const span = document.createElement('span');

      if (row.date) {
        span.textContent = new Date(row.date).toLocaleString();
      }

      return span;
    }
  },
]);

// Spread to create a new array reference when selectedCommitId changes,
// so Lit's hasChanged detects the update and re-renders the radio buttons.
const tableRows = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _sid = selectedCommitId.value;

  return [...preparedCommits.value];
});

// Watch the account/org and perform a debounced search when it changes
watch(() => selectedAccOrOrg.value, async(neu, old) => {
  if (neu === old) return;
  debouncedFetchRepos();
});

// Watch the repo and fetch branches when it changes
watch(() => selectedRepo.value, async(neu, old) => {
  if (neu === old) return;
  await fetchBranches();
});

// Watch the branch and fetch commits when it changes
watch(() => selectedBranch.value, async(neu, old) => {
  if (neu === old) return;
  await fetchCommits();
});

// Methods
function normalizeArray(elem: any, normalize: (v: any) => object): any[] {
  const arr = isArray(elem) ? elem : [elem];

  return arr.map((item: any) => normalize(item));
}

function communicateReset() {
  emit('change', {
    selectedAccOrOrg: selectedAccOrOrg.value,
    repo:             selectedRepo.value,
    commit:           selectedCommit.value,
  });
}

function reset() {
  repos.value = [];
  selectedAccOrOrg.value = undefined;
  selectedRepo.value = undefined;
  selectedBranch.value = undefined;
  selectedCommit.value = null;
  communicateReset();
}

function final(commitId: string) {
  selectedCommit.value = preparedCommits.value.find((c: any) => c.commitId === commitId) || null;

  if (selectedAccOrOrg.value && selectedRepo.value && selectedCommit.value?.commitId) {
    emit('change', {
      selectedAccOrOrg: selectedAccOrOrg.value,
      repo:             selectedRepo.value,
      branch:           selectedBranch.value,
      commit:           selectedCommit.value.commitId,
      sourceData:       {
        repos:    repos.value,
        branches: branches.value,
        commits:  commits.value,
      }
    });
  }
}

async function fetchRepos() {
  repos.value = [];
  selectedRepo.value = undefined;
  selectedBranch.value = undefined;
  selectedCommit.value = null;
  communicateReset();

  if (selectedAccOrOrg.value?.length) {
    try {
      const res = await store.dispatch(`${ props.type }/fetchRecentRepos`, { username: selectedAccOrOrg.value });

      repos.value = res;
      hasError.acc = false;
    } catch {
      hasError.acc = true;
    }
  }
}

const debouncedFetchRepos = debounce(fetchRepos, debounceTime);

async function fetchBranches() {
  selectedBranch.value = undefined;
  selectedCommit.value = null;
  communicateReset();

  try {
    const res = await store.dispatch(`${ props.type }/fetchBranches`, {
      repo:     selectedRepo.value,
      username: selectedAccOrOrg.value,
    });

    branches.value = res;
    hasError.branch = false;
  } catch {
    hasError.branch = true;
  }
}

async function fetchCommits() {
  selectedCommit.value = null;
  communicateReset();

  try {
    const res = await store.dispatch(`${ props.type }/fetchCommits`, {
      repo:     selectedRepo.value,
      username: selectedAccOrOrg.value,
      branch:   selectedBranch.value,
    });

    commits.value = res as any[];
    hasError.branch = false;
  } catch {
    hasError.commits = true;
  }
}

async function loadSourceCache(accOrOrg: string, repo: any, branch: any, commit: any) {
  selectedAccOrOrg.value = accOrOrg;

  if (selectedAccOrOrg.value) {
    await fetchRepos()
      .then(() => {
        if (repos.value.length && !hasError.repo) {
          selectedRepo.value = repo;

          return fetchBranches();
        }
      })
      .then(() => {
        if (branches.value.length && !hasError.branch) {
          if (branch?.name) {
            selectedBranch.value = branch;

            return fetchCommits();
          }
        }
      });

    const foundCommit = commits.value?.find((c: any) => {
      const sha = c.sha || c.id;

      return sha === commit.sha;
    });

    if (foundCommit) {
      final(foundCommit.sha || foundCommit.id);
    }
  }
}

async function searchRepo(query: string) {
  if (query.length) {
    const resultInCurrentState = repos.value.filter((r: any) => r.name.startsWith(query));

    if (!resultInCurrentState.length) {
      const res = await store.dispatch(`${ props.type }/search`, {
        repo:     { id: query, name: query },
        username: selectedAccOrOrg.value,
      });

      if (!res.hasError) {
        repos.value = [...repos.value, res[0]];
      }
    } else {
      return resultInCurrentState;
    }
  }
}

const debouncedSearchRepo = debounce(searchRepo, debounceTime);

async function searchBranch(query: string) {
  if (query.length) {
    const resultInCurrentState = branches.value.filter((b: any) => b.name.startsWith(query));

    if (!resultInCurrentState.length) {
      const res = await store.dispatch(`${ props.type }/search`, {
        repo:     selectedRepo.value,
        branch:   { name: query },
        username: selectedAccOrOrg.value,
      });

      if (!hasError.branch) {
        branches.value = [...branches.value, res[0]];
      }
    } else {
      return resultInCurrentState;
    }
  }
}

const debouncedSearchBranch = debounce(searchBranch, debounceTime);

watch(() => props.value, async(neu, old) => {
  if (JSON.stringify(neu) === JSON.stringify(old)) return;
  if (neu?.type !== old?.type) {
    reset();
    await loadSourceCache(neu.selectedAccOrOrg, neu.selectedRepo, neu.selectedBranch, neu.selectedCommit);
  }
}, { immediate: true, deep: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("trailhand-text-input", {
          style: {"width":"100%"},
          value: selectedAccOrOrg.value,
          "data-testid": "git_picker-username-or-org",
          label: _unref(t)(`gitPicker.${ _ctx.type }.username.inputLabel`),
          required: true,
          onTextInputChange: _cache[0] || (_cache[0] = (e) => { selectedAccOrOrg.value = e.detail.value; })
        }, null, 40, _hoisted_4),
        _cache[5] || (_cache[5] = _createTextVNode()),
        (hasError.acc)
          ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_unref(t)(`gitPicker.${ _ctx.type }.errors.noAccount`)), 1))
          : _createCommentVNode("", true)
      ]),
      _cache[6] || (_cache[6] = _createTextVNode()),
      (repos.value.length && !hasError.repo)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("trailhand-dropdown", {
              style: {"width":"100%"},
              value: selectedRepoName.value,
              "data-testid": "git_picker-repo",
              label: _unref(t)(`gitPicker.${ _ctx.type }.repo.inputLabel`),
              required: true,
              options: preparedRepos.value,
              filterable: "",
              onDropdownChange: _cache[1] || (_cache[1] = (e) => { 
            const selected = repos.value.find((r) => r.name === e.detail.value);
            selectedRepo.value = selected; 
          }),
              onDropdownFilter: _cache[2] || (_cache[2] = (e) => { _unref(debouncedSearchRepo)(e.detail.filter); })
            }, null, 40, _hoisted_7)
          ]))
        : _createCommentVNode("", true),
      _cache[7] || (_cache[7] = _createTextVNode()),
      (selectedRepo.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("trailhand-dropdown", {
              style: {"width":"100%"},
              value: selectedBranchName.value,
              "data-testid": "git_picker-branch",
              label: _unref(t)(`gitPicker.${ _ctx.type }.branch.inputLabel`),
              required: true,
              options: preparedBranches.value,
              filterable: "",
              onDropdownChange: _cache[3] || (_cache[3] = (e) => { 
            const selected = branches.value.find((b) => b.name === e.detail.value);
            selectedBranch.value = selected; 
          }),
              onDropdownFilter: _cache[4] || (_cache[4] = (e) => { _unref(debouncedSearchBranch)(e.detail.filter); })
            }, null, 40, _hoisted_9)
          ]))
        : _createCommentVNode("", true),
      _cache[8] || (_cache[8] = _createTextVNode()),
      (selectedBranch.value && preparedCommits.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createElementVNode("trailhand-table", {
              rows: tableRows.value,
              columns: columns.value,
              "key-field": "sha",
              searchable: true,
              paginated: true,
              "rows-per-page": 10
            }, null, 8, _hoisted_11)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})